import { useEffect, useState } from "react";
import {
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalFooter,
  MDBModalHeader,
  MDBTable,
} from "mdbreact";
import { GenericProducts } from "../../../../constants";
import { lineName, poNumber, qtyLabel, unitLabel } from "./common";

/**
 * Approve (adjust approved qty, or deny) or receive (received qty + lot/expiry)
 * a purchase order. `mode`: "approve" | "receive"
 */
export default function ActionModal({
  mode,
  order,
  toggle,
  onSubmit,
  isLoading,
  // receive: qty received / free entered on the list, used as the start values
  receiveQtyOf,
  freeQtyOf,
}) {
  const [lines, setLines] = useState([]),
    [response, setResponse] = useState(""),
    [received, setReceived] = useState("");

  const isReceive = mode === "receive";

  useEffect(() => {
    if (!order) return;
    setResponse("");
    setReceived(new Date().toISOString().slice(0, 10));
    setLines(
      (order.items || [])
        // denied items (approved 0) are not delivered, so not received
        .filter((item) => !isReceive || item.qnty.approved !== 0)
        .map((item) => {
          const value = isReceive
            ? (receiveQtyOf?.(item) ?? item.qnty.approved ?? item.qnty.request)
            : item.qnty.request;
          const free = isReceive ? Number(freeQtyOf?.(item)) || 0 : 0;
          return {
            ...item,
            value,
            free,
            // receive: price per packaging unit on the delivery receipt
            price: item.price ?? "",
            // approve only: an item denied by itself, with its reason
            denied: false,
            reason: "",
            // brands with lot tracking start with one lot row for the whole qty, free included
            lots:
              isReceive && item.productId?.tracksLot
                ? [{ lot: "", expiry: "", qty: Number(value) + free }]
                : [],
          };
        }),
    );
    // start values are read once when the order opens, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, isReceive]);

  // a single lot follows the received + free qty
  const syncLot = (line, changes) => {
    const next = { ...line, ...changes };
    return isReceive && line.lots.length === 1
      ? {
          ...changes,
          lots: [
            {
              ...line.lots[0],
              qty: (Number(next.value) || 0) + (Number(next.free) || 0),
            },
          ],
        }
      : changes;
  };

  const update = (index, changes) =>
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, ...changes } : line)),
    );

  const updateLot = (index, lotIndex, changes) =>
    update(index, {
      lots: lines[index].lots.map((lot, i) =>
        i === lotIndex ? { ...lot, ...changes } : lot,
      ),
    });

  // lots, when given, must add up to the received quantity, free included
  const lotMismatch = lines.filter(
    ({ lots, value, free }) =>
      lots.length &&
      lots.reduce((sum, { qty }) => sum + (Number(qty) || 0), 0) !==
        (Number(value) || 0) + (Number(free) || 0),
  );

  // brands with lot tracking need a lot number and expiry on every lot
  const lotMissing = isReceive
    ? lines.filter(
        ({ productId, value, free, lots }) =>
          productId?.tracksLot &&
          (Number(value) || 0) + (Number(free) || 0) > 0 &&
          (!lots.length ||
            lots.some(({ lot, expiry }) => !lot?.trim() || !expiry)),
      )
    : [];

  const payload = (extra = {}) => ({
    _id: order._id,
    response,
    ...(isReceive && { received }),
    items: lines.map(({ _id, value, free, price, lots, denied, reason }) => ({
      _id,
      [isReceive ? "received" : "approved"]: denied ? 0 : Number(value) || 0,
      ...(isReceive && {
        lots,
        freeItem: Number(free) || 0,
        price: Number(price) || 0,
      }),
      ...(!isReceive && denied && reason.trim() && { remarks: reason.trim() }),
    })),
    ...extra,
  });

  // approve: items left to approve (not denied, qty above 0)
  const approving = lines.filter(
    ({ denied, value }) => !denied && Number(value) > 0,
  );

  if (!order) return null;

  return (
    <MDBModal isOpen={!!order} toggle={toggle} backdrop size="lg">
      <MDBModalHeader
        toggle={toggle}
        className="light-blue darken-3 white-text"
      >
        <MDBIcon icon={isReceive ? "truck" : "check-double"} className="mr-2" />
        {isReceive ? "Receive" : "Approve"} {poNumber(order)}
      </MDBModalHeader>
      <MDBModalBody>
        {order.remarks && (
          <p className="small mb-2">
            <b>Remarks:</b> {order.remarks}
          </p>
        )}
        <MDBTable small bordered className="mb-2">
          <thead className="grey lighten-4">
            <tr>
              <th>Item</th>
              {/* receive: approved | received | free | price | variant (unit) */}
              {isReceive ? (
                <th style={{ width: 90 }} className="text-center">
                  Approved
                </th>
              ) : (
                <th style={{ width: 140 }}>Requested</th>
              )}
              <th style={{ width: 120 }}>
                {isReceive ? "Received" : "Approve"}
              </th>
              {isReceive ? (
                <>
                  <th
                    style={{ width: 80 }}
                    title="Free items given by the supplier, e.g. 6+1"
                  >
                    Free
                  </th>
                  <th style={{ width: 110 }} title="Price per unit received">
                    Price
                  </th>
                  <th style={{ width: 150 }}>Variant</th>
                </>
              ) : (
                <th style={{ width: 90 }} />
              )}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const generic = GenericProducts.find(line.pgid);
              return [
                <tr
                  key={line._id}
                  className={line.denied ? "red lighten-5" : ""}
                >
                  <td>
                    {generic && (
                      <code className="mr-1">{generic.abbreviation}</code>
                    )}
                    <b
                      style={{
                        textDecoration: line.denied ? "line-through" : "none",
                      }}
                    >
                      {lineName(line)}
                    </b>
                    {line.denied && (
                      <input
                        className="form-control form-control-sm mt-1"
                        placeholder="Reason for denying this item (optional)"
                        value={line.reason}
                        onChange={({ target }) =>
                          update(index, { reason: target.value })
                        }
                      />
                    )}
                    {isReceive && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 m-0 ml-2"
                        style={{ textTransform: "none" }}
                        onClick={() =>
                          update(index, {
                            lots: [
                              ...line.lots,
                              { lot: "", expiry: "", qty: "" },
                            ],
                          })
                        }
                      >
                        + lot / expiry
                      </button>
                    )}
                  </td>
                  {isReceive ? (
                    <td className="align-middle text-center font-weight-bold">
                      {line.qnty.approved ?? line.qnty.request}
                    </td>
                  ) : (
                    <td className="small align-middle">
                      {qtyLabel(line.qnty.request, line)}
                    </td>
                  )}
                  <td>
                    <input
                      type="number"
                      min={0}
                      className="form-control form-control-sm"
                      value={line.denied ? 0 : line.value}
                      disabled={line.denied}
                      onChange={({ target }) =>
                        update(index, syncLot(line, { value: target.value }))
                      }
                    />
                    {isReceive &&
                      (() => {
                        const diff =
                          Number(line.value || 0) -
                          Number(line.qnty.approved ?? line.qnty.request);
                        return diff ? (
                          <small
                            className={`d-block ${
                              diff < 0 ? "text-danger" : "text-primary"
                            }`}
                          >
                            {diff < 0 ? `${-diff} short` : `${diff} more`}
                          </small>
                        ) : null;
                      })()}
                  </td>
                  {isReceive && (
                    <>
                      <td>
                        <input
                          type="number"
                          min={0}
                          className="form-control form-control-sm"
                          value={line.free}
                          title="Free items (same unit), e.g. 1 for 6+1"
                          onChange={({ target }) =>
                            update(index, syncLot(line, { free: target.value }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          className="form-control form-control-sm"
                          placeholder="0.00"
                          value={line.price}
                          title={`Price per ${line.unit || "unit"}`}
                          onChange={({ target }) =>
                            update(index, { price: target.value })
                          }
                        />
                      </td>
                      <td className="small align-middle text-muted">
                        {unitLabel(line)}
                      </td>
                    </>
                  )}
                  {!isReceive && (
                    <td className="align-middle text-center">
                      <button
                        type="button"
                        className={`btn btn-sm m-0 px-2 py-1 z-depth-0 ${
                          line.denied
                            ? "btn-outline-success"
                            : "btn-outline-danger"
                        }`}
                        style={{ textTransform: "none" }}
                        title={
                          line.denied
                            ? "Approve this item again"
                            : "Deny this item only"
                        }
                        onClick={() => update(index, { denied: !line.denied })}
                      >
                        <MDBIcon
                          icon={line.denied ? "undo" : "ban"}
                          className="mr-1"
                        />
                        {line.denied ? "Undo" : "Deny"}
                      </button>
                    </td>
                  )}
                </tr>,
                ...line.lots.map((lot, lotIndex) => (
                  <tr
                    key={`${line._id}-lot-${lotIndex}`}
                    className="grey lighten-5"
                  >
                    <td colSpan={2}>
                      <div
                        className="d-flex align-items-center pl-3"
                        style={{ gap: 6 }}
                      >
                        <MDBIcon
                          icon="level-up-alt"
                          rotate="90"
                          className="text-muted"
                        />
                        <input
                          className="form-control form-control-sm"
                          placeholder="Lot no."
                          value={lot.lot}
                          onChange={({ target }) =>
                            updateLot(index, lotIndex, { lot: target.value })
                          }
                        />
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          title="Expiry"
                          value={lot.expiry}
                          onChange={({ target }) =>
                            updateLot(index, lotIndex, { expiry: target.value })
                          }
                        />
                        <MDBIcon
                          icon="times"
                          className="text-danger"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            update(index, {
                              lots: line.lots.filter((_, i) => i !== lotIndex),
                            })
                          }
                        />
                      </div>
                    </td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        className="form-control form-control-sm"
                        placeholder="Qty"
                        value={lot.qty}
                        onChange={({ target }) =>
                          updateLot(index, lotIndex, { qty: target.value })
                        }
                      />
                    </td>
                    <td colSpan={3} className="small text-muted align-middle">
                      received + free
                    </td>
                  </tr>
                )),
              ];
            })}
          </tbody>
        </MDBTable>

        {lotMissing.length > 0 && (
          <div className="alert alert-danger py-1 px-2 small">
            Enter the lot number and expiry of{" "}
            <b>{lotMissing.map(lineName).join(", ")}</b>.
          </div>
        )}

        {lotMismatch.length > 0 && (
          <div className="alert alert-warning py-1 px-2 small">
            The lots of <b>{lotMismatch.map(lineName).join(", ")}</b> must add
            up to the received quantity, free included.
          </div>
        )}

        {isReceive ? (
          <div style={{ maxWidth: 200 }}>
            <label className="small text-muted mb-1 d-block">
              Date received
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={received}
              onChange={({ target }) => setReceived(target.value)}
            />
          </div>
        ) : (
          <>
            <label className="small text-muted mb-1 d-block">
              Response / reason
            </label>
            <input
              className="form-control form-control-sm"
              value={response}
              placeholder="optional, required when denying"
              onChange={({ target }) => setResponse(target.value)}
            />
          </>
        )}
      </MDBModalBody>
      <MDBModalFooter className="py-1">
        <MDBBtn size="sm" color="light" onClick={toggle}>
          Close
        </MDBBtn>
        {!isReceive && (
          <MDBBtn
            size="sm"
            color="danger"
            onClick={() => onSubmit("deny", { _id: order._id, response })}
            disabled={isLoading || !response.trim()}
            title={response.trim() ? "" : "Write the reason first"}
          >
            <MDBIcon icon="ban" className="mr-1" /> Deny whole request
          </MDBBtn>
        )}
        <MDBBtn
          size="sm"
          color="primary"
          onClick={() => onSubmit(isReceive ? "receive" : "approve", payload())}
          disabled={
            isLoading ||
            lotMismatch.length > 0 ||
            lotMissing.length > 0 ||
            // every item denied: deny the whole request instead
            (!isReceive && !approving.length)
          }
          title={
            !isReceive && !approving.length
              ? "Every item is denied, use Deny for the whole request"
              : undefined
          }
        >
          <MDBIcon icon={isReceive ? "box-open" : "check"} className="mr-1" />
          {isReceive
            ? "Receive & add to stock"
            : `Approve ${approving.length} of ${lines.length} item(s)`}
        </MDBBtn>
      </MDBModalFooter>
    </MDBModal>
  );
}
